import { SavedReportControls, type ReportFilters } from './SavedReportControls';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  api, ApiError,
  type CohortOption, type CourseReportRow, type ExpiringRow, type GradeCell, type Gradebook,
  type MatrixCell, type MatrixReport, type PersonReportRow,
} from '../../api';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const pct = (n: number | null) => (n == null ? '' : `${n}%`);

/** The CSV comes from the same URL as the table, so the file always matches what is on screen. */
function download(path: string) {
  window.location.href = `/api${path}${path.includes('?') ? '&' : '?'}format=csv`;
}

type Tab = 'courses' | 'gradebook' | 'people' | 'matrix' | 'expiring';

export function Reports() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('tab') ?? 'courses';
  const tab: Tab = ['courses', 'gradebook', 'people', 'matrix', 'expiring'].includes(requestedTab) ? requestedTab as Tab : 'courses';
  const cohortId = params.get('cohort') ?? '';
  const days = params.get('days') ?? '90';

  const [cohorts, setCohorts] = useState<CohortOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setCohorts((await api.get<{ cohorts: CohortOption[] }>('/admin/reports/cohorts')).cohorts); }
      catch { /* the reports still work without the filter */ }
    })();
  }, []);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const filters: ReportFilters = tab === 'courses' ? {}
    : tab === 'expiring' ? { days: Number(days) }
    : tab === 'gradebook' ? { courseId: Number(params.get('course')), ...(cohortId ? { groupId: Number(cohortId) } : {}) }
    : { ...(cohortId ? { groupId: Number(cohortId) } : {}), ...(tab === 'people' ? { ...(params.get('status') ? { status: params.get('status')! } : {}), ...(params.get('nophone') === '1' ? { missingPhone: true } : {}) } : {}) };

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Reports</h1>
      </div>

      {error && <p className="auth__error">{error}</p>}

      <div className="tabs">
        {([['courses', 'Courses'], ['gradebook', 'Gradebook'], ['people', 'People'], ['matrix', 'Training matrix'], ['expiring', 'Expiring']] as const)
          .map(([key, label]) => (
            <button key={key} className={`tabs__tab ${tab === key ? 'is-on' : ''}`} onClick={() => set('tab', key)}>
              {label}
            </button>
          ))}
      </div>

      <SavedReportControls key={tab} kind={tab} filters={filters} disabled={tab === 'gradebook' && !params.get('course')} />
      {tab === 'courses' && <CoursesReport onError={setError} />}
      {tab === 'gradebook' && (
        <GradebookView
          courseId={params.get('course') ?? ''} onCourse={(v) => set('course', v)}
          cohorts={cohorts} cohortId={cohortId} onCohort={(v) => set('cohort', v)}
          onError={setError}
        />
      )}
      {tab === 'people' && (
        <PeopleReport
          cohorts={cohorts} cohortId={cohortId} onCohort={(v) => set('cohort', v)}
          accountStatus={params.get('status') ?? ''} onStatus={(v) => set('status', v)}
          missingPhone={params.get('nophone') === '1'}
          onMissingPhone={(v) => set('nophone', v ? '1' : '')}
          onError={setError}
        />
      )}
      {tab === 'matrix' && <MatrixView cohorts={cohorts} cohortId={cohortId} onCohort={(v) => set('cohort', v)} onError={setError} />}
      {tab === 'expiring' && <ExpiringReport days={days} onDays={(v) => set('days', v)} onError={setError} />}
    </>
  );
}

function CoursesReport({ onError }: { onError: (m: string | null) => void }) {
  const [rows, setRows] = useState<CourseReportRow[] | null>(null);

  useEffect(() => {
    void (async () => {
      try { setRows((await api.get<{ courses: CourseReportRow[] }>('/admin/reports/courses')).courses); onError(null); }
      catch (e) { onError((e as ApiError).message); }
    })();
  }, [onError]);

  if (!rows) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="filters">
        <span className="muted">{rows.length} courses</span>
        <button className="btn btn--ghost btn--small" onClick={() => download('/admin/reports/courses')}>
          Download CSV
        </button>
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th>Course</th><th>Enrolled</th><th>Completed</th><th>In progress</th><th>Not started</th>
            <th>Completion</th><th>Avg score</th><th>Avg days</th><th>Overdue</th><th>To mark</th><th>Certificates</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className={c.status !== 'active' ? 'is-off' : ''}>
              <td>
                <Link to={`?tab=gradebook&course=${c.id}`}>{c.name}</Link>
                {c.status !== 'active' && <span className="tag">off</span>}
              </td>
              <td className="num">{c.enrolled}</td>
              <td className="num">{c.completed}</td>
              <td className="num">{c.inProgress}</td>
              <td className="num">{c.notStarted}</td>
              <td className="num">{pct(c.completionRate)}</td>
              <td className="num">{pct(c.avgScore)}</td>
              <td className="num">{c.avgDays ?? ''}</td>
              <td className="num">{c.overdue > 0 ? <strong className="bad">{c.overdue}</strong> : 0}</td>
              <td className="num">{Number(c.awaitingMarking) > 0 ? <strong className="bad">{c.awaitingMarking}</strong> : 0}</td>
              <td className="num">{c.certificates}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">
        “Avg days” is how long the people who finished took from being enrolled. “Overdue” counts anyone past
        the course's own time limit who has not finished.
      </p>
    </>
  );
}

function PeopleReport({ cohorts, cohortId, onCohort, accountStatus, onStatus, missingPhone, onMissingPhone, onError }: {
  cohorts: CohortOption[]; cohortId: string; onCohort: (v: string) => void;
  accountStatus: string; onStatus: (v: string) => void;
  missingPhone: boolean; onMissingPhone: (v: boolean) => void;
  onError: (m: string | null) => void;
}) {
  const [rows, setRows] = useState<PersonReportRow[] | null>(null);
  const [withoutPhone, setWithoutPhone] = useState(0);
  const search = new URLSearchParams();
  if (cohortId) search.set('groupId', cohortId);
  if (accountStatus) search.set('status', accountStatus);
  if (missingPhone) search.set('missingPhone', 'true');
  const query = search.toString() ? `?${search}` : '';

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ people: PersonReportRow[]; withoutPhone: number }>(`/admin/reports/people${query}`);
      setRows(res.people); setWithoutPhone(res.withoutPhone ?? 0); onError(null);
    } catch (e) { onError((e as ApiError).message); }
  }, [query, onError]);
  useEffect(() => { void load(); }, [load]);

  if (!rows) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="filters">
        <select value={cohortId} onChange={(e) => onCohort(e.target.value)} aria-label="Cohort">
          <option value="">Every cohort</option>
          {cohorts.map((c) => <option key={c.id} value={String(c.id)}>{c.name} ({c.members})</option>)}
        </select>
        <select value={accountStatus} onChange={(e) => onStatus(e.target.value)} aria-label="Account">
          <option value="">Active and deactivated</option>
          <option value="active">Active accounts only</option>
          <option value="inactive">Deactivated only</option>
        </select>
        <label className="check">
          <input type="checkbox" checked={missingPhone} onChange={(e) => onMissingPhone(e.target.checked)} />
          No phone number
        </label>
        <span className="muted">{rows.length} people</span>
        <button className="btn btn--ghost btn--small" onClick={() => download(`/admin/reports/people${query}`)}>
          Download CSV
        </button>
      </div>

      {!missingPhone && withoutPhone > 0 && (
        <p className="muted small">
          {withoutPhone} of these have no phone number, so WhatsApp cannot reach them. Tick “No phone number”
          to list them, or download the CSV and work through it.
        </p>
      )}
      <table className="grid">
        <thead>
          <tr>
            <th>Person</th><th>Phone</th><th>Account</th><th>Assigned</th><th>Completed</th><th>In progress</th>
            <th>Not started</th><th>Overdue</th><th>Avg score</th><th>Certificates</th><th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className={p.status !== 'active' ? 'is-off' : ''}>
              <td>{p.firstName} {p.lastName} <span className="muted small mono">{p.login}</span></td>
              <td className="mono small">{p.phone ?? <span className="warn">none</span>}</td>
              <td>{p.status === 'active' ? 'active' : <span className="muted">deactivated</span>}</td>
              <td className="num">{p.assigned}</td>
              <td className="num">{p.completed}</td>
              <td className="num">{p.inProgress}</td>
              <td className="num">{p.notStarted}</td>
              <td className="num">{p.overdue > 0 ? <strong className="bad">{p.overdue}</strong> : 0}</td>
              <td className="num">{pct(p.avgScore)}</td>
              <td className="num">{p.certificates}</td>
              <td>{day(p.lastActivity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * People down the side, courses across the top — the sheet a client or an
 * auditor asks for. Kept to a cohort by default, because the whole portal at
 * once is unreadable.
 */
function MatrixView({ cohorts, cohortId, onCohort, onError }: {
  cohorts: CohortOption[]; cohortId: string; onCohort: (v: string) => void; onError: (m: string | null) => void;
}) {
  const [data, setData] = useState<MatrixReport | null>(null);
  const query = cohortId ? `?groupId=${cohortId}` : '';

  const load = useCallback(async () => {
    try { setData(await api.get<MatrixReport>(`/admin/reports/matrix${query}`)); onError(null); }
    catch (e) { onError((e as ApiError).message); }
  }, [query, onError]);
  useEffect(() => { void load(); }, [load]);

  if (!data) return <p className="muted">Loading…</p>;

  /** One cell of the grid, keyed by its course. */
  const cell = (c: MatrixCell | undefined, courseId: number) => {
    if (!c) return <td key={courseId} className="cellx" title="not assigned">—</td>;
    if (c.status === 'completed') {
      return (
        <td key={courseId} className="cell cell--done" title={c.expiresAt ? `expires ${day(c.expiresAt)}` : 'completed'}>
          ✓{c.expiresAt ? <span className="cell__sub">{day(c.expiresAt)}</span> : null}
        </td>
      );
    }
    if (c.status === 'failed') return <td key={courseId} className="cell cell--fail" title="failed">✗</td>;
    if (c.status === 'pending_review') return <td key={courseId} className="cell cell--wait" title="waiting to be marked">…</td>;
    if (c.status === 'not_attempted') return <td key={courseId} className="cell cell--none" title="not started">·</td>;
    return (
      <td key={courseId} className={`cell ${c.overdue ? 'cell--late' : 'cell--part'}`}
        title={c.overdue ? 'overdue' : 'in progress'}>
        {c.pct}%
      </td>
    );
  };

  return (
    <>
      <div className="filters">
        <select value={cohortId} onChange={(e) => onCohort(e.target.value)} aria-label="Cohort">
          <option value="">All live courses</option>
          {cohorts.map((c) => <option key={c.id} value={String(c.id)}>{c.name} ({c.members})</option>)}
        </select>
        <span className="muted">{data.people.length} people · {data.courses.length} courses</span>
        <button className="btn btn--ghost btn--small" onClick={() => download(`/admin/reports/matrix${query}`)}>
          Download CSV
        </button>
      </div>

      {data.note && <p className="notice">{data.note}</p>}

      <div className="matrix__legend muted small">
        <span className="cell cell--done">✓</span> completed
        <span className="cell cell--part">%</span> in progress
        <span className="cell cell--late">%</span> overdue
        <span className="cell cell--wait">…</span> waiting to be marked
        <span className="cell cell--none">·</span> not started
        <span className="cellx">—</span> not assigned
      </div>

      {data.courses.length > 0 && (
        <div className="matrix__scroll">
          <table className="grid matrix">
            <thead>
              <tr>
                <th className="matrix__name">Person</th>
                {data.courses.map((c) => <th key={c.id} className="matrix__head"><span>{c.name}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) => (
                <tr key={p.userId}>
                  <td className="matrix__name">{p.firstName} {p.lastName}</td>
                  {data.courses.map((c) => cell(p.cells[String(c.id)], c.id))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * One course: its trainees down the side, its tests and assignments across,
 * and in each cell the mark that counts, how many tries it took and whether
 * it was passed. The bottom line is how each test went across everybody.
 */
function GradebookView({ courseId, onCourse, cohorts, cohortId, onCohort, onError }: {
  courseId: string; onCourse: (v: string) => void;
  cohorts: CohortOption[]; cohortId: string; onCohort: (v: string) => void;
  onError: (m: string | null) => void;
}) {
  const [courses, setCourses] = useState<CourseReportRow[] | null>(null);
  const [data, setData] = useState<Gradebook | null>(null);
  const query = courseId ? `?courseId=${courseId}${cohortId ? `&groupId=${cohortId}` : ''}` : '';

  useEffect(() => {
    void (async () => {
      try { setCourses((await api.get<{ courses: CourseReportRow[] }>('/admin/reports/courses')).courses); }
      catch (e) { onError((e as ApiError).message); }
    })();
  }, [onError]);

  const load = useCallback(async () => {
    if (!query) { setData(null); return; }
    setData(null);
    try { setData(await api.get<Gradebook>(`/admin/reports/gradebook${query}`)); onError(null); }
    catch (e) { onError((e as ApiError).message); }
  }, [query, onError]);
  useEffect(() => { void load(); }, [load]);

  const cell = (c: GradeCell | undefined, unitId: number, passMark: number | null) => {
    if (!c) return <td key={unitId} className="cellx" title="not taken">—</td>;
    const tries = c.attempts > 1 ? `${c.attempts} tries` : c.attempts === 1 ? '1 try' : '';
    const mark = c.score == null ? '' : `${Math.round(c.score)}%`;
    if (c.result === 'started') return <td key={unitId} className="cell cell--none" title="opened, nothing handed in">·</td>;
    if (c.result === 'waiting') {
      return <td key={unitId} className="cell cell--wait" title="waiting to be marked">…<span className="cell__sub">{tries}</span></td>;
    }
    const passed = c.result === 'passed';
    return (
      <td key={unitId} className={`cell ${passed ? 'cell--done' : 'cell--fail'}`}
        title={`${passed ? 'passed' : 'failed'}${mark ? ` with ${mark}` : ''}${passMark != null ? ` (pass mark ${passMark}%)` : ''}${tries ? `, ${tries}` : ''}`}>
        {mark || (passed ? '✓' : '✗')}<span className="cell__sub">{tries}</span>
      </td>
    );
  };

  return (
    <>
      <div className="filters">
        <select value={courseId} onChange={(e) => onCourse(e.target.value)} aria-label="Course">
          <option value="">Choose a course…</option>
          {(courses ?? []).map((c) => (
            <option key={c.id} value={String(c.id)}>{c.name}{c.status !== 'active' ? ' (off)' : ''}</option>
          ))}
        </select>
        <select value={cohortId} onChange={(e) => onCohort(e.target.value)} aria-label="Cohort">
          <option value="">Everybody on it</option>
          {cohorts.map((c) => <option key={c.id} value={String(c.id)}>{c.name} ({c.members})</option>)}
        </select>
        {data && <span className="muted">{data.people.length} trainees · {data.units.length} tests and assignments</span>}
        {data && (
          <button className="btn btn--ghost btn--small" onClick={() => download(`/admin/reports/gradebook${query}`)}>
            Download CSV
          </button>
        )}
      </div>

      {!courseId && <p className="notice">Choose a course to see every trainee’s marks on its tests and assignments.</p>}
      {courseId && !data && <p className="muted">Loading…</p>}

      {data && data.units.length === 0 && (
        <p className="notice">{data.course.name} has no tests or assignments, so there are no marks to show. Its progress is in the Courses and Training matrix reports.</p>
      )}

      {data && data.units.length > 0 && (
        <>
          <div className="matrix__legend muted small">
            <span className="cell cell--done">%</span> passed
            <span className="cell cell--fail">%</span> failed
            <span className="cell cell--wait">…</span> waiting to be marked
            <span className="cell cell--none">·</span> opened, not handed in
            <span className="cellx">—</span> not taken
          </div>
          <div className="matrix__scroll">
            <table className="grid matrix gradebook">
              <thead>
                <tr>
                  <th className="matrix__name">Trainee</th>
                  <th className="matrix__head"><span>Course score</span></th>
                  {data.units.map((u) => (
                    <th key={u.id} className="matrix__head" title={u.isActive ? u.name : `${u.name} (switched off)`}>
                      <span>{u.name}{u.isActive ? '' : ' (off)'}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.userId}>
                    <td className="matrix__name">
                      <Link to={`/progress/${p.userId}`}>{p.firstName} {p.lastName}</Link>
                    </td>
                    <td className="cell">{p.score == null ? '' : `${Math.round(p.score)}%`}<span className="cell__sub">{p.completionPct}% done</span></td>
                    {data.units.map((u) => cell(p.cells[String(u.id)], u.id, u.passMark))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="matrix__name">Everybody</th>
                  <td />
                  {data.units.map((u) => {
                    const t = data.summary[String(u.id)];
                    return (
                      <td key={u.id} className="cell" title={`${t.taken} sat it, ${t.passed} passed${t.waiting ? `, ${t.waiting} waiting to be marked` : ''}`}>
                        {t.average == null ? '' : `${t.average}%`}
                        <span className="cell__sub">{t.passed}/{t.taken} passed</span>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="muted small">
            The mark is the one that counts towards the course score. Hover over a cell for the pass mark and the
            number of tries. A name opens that trainee’s own progress page.
          </p>
        </>
      )}
    </>
  );
}

function ExpiringReport({ days, onDays, onError }: {
  days: string; onDays: (v: string) => void; onError: (m: string | null) => void;
}) {
  const [data, setData] = useState<{ expired: number; expiringSoon: number; certificates: ExpiringRow[] } | null>(null);
  const query = `?days=${encodeURIComponent(days)}`;

  const load = useCallback(async () => {
    try { setData(await api.get(`/admin/reports/expiring${query}`)); onError(null); }
    catch (e) { onError((e as ApiError).message); }
  }, [query, onError]);
  useEffect(() => { void load(); }, [load]);

  if (!data) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="filters">
        <select value={days} onChange={(e) => onDays(e.target.value)} aria-label="Window">
          <option value="30">Next 30 days</option>
          <option value="60">Next 60 days</option>
          <option value="90">Next 90 days</option>
          <option value="180">Next 6 months</option>
          <option value="365">Next year</option>
        </select>
        <span className="muted">{data.expired} already expired · {data.expiringSoon} due</span>
        <button className="btn btn--ghost btn--small" onClick={() => download(`/admin/reports/expiring${query}`)}>
          Download CSV
        </button>
      </div>

      {data.certificates.length === 0
        ? <p className="notice">Nothing expires in that window.</p>
        : (
          <table className="grid">
            <thead>
              <tr><th>Person</th><th>Course</th><th>Issued</th><th>Expires</th><th>Days left</th><th>Standing</th></tr>
            </thead>
            <tbody>
              {data.certificates.map((r) => (
                <tr key={r.id}>
                  <td>{r.firstName} {r.lastName} <span className="muted small mono">{r.login}</span></td>
                  <td>{r.courseName}</td>
                  <td>{day(r.issuedAt)}</td>
                  <td>{day(r.expiresAt)}</td>
                  <td className="num">{r.daysLeft != null && r.daysLeft < 0 ? <strong className="bad">{r.daysLeft}</strong> : r.daysLeft}</td>
                  <td>
                    {r.standing === 'valid'
                      ? <span className="pill pill--completed">valid</span>
                      : <span className="pill pill--incomplete">expired</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      <p className="muted small">
        Refresher training is the usual answer: enrol the person again on the course and a new certificate is
        issued when they finish it.
      </p>
    </>
  );
}
